import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { getUserRBAC, resetRbacStatus } from '../../store/authSlice';
import { AppLayout } from '@/shared/layouts';
import { AppLoader } from '@/shared/components';
import { buttonClassName } from '@/shared/components/ui/button/buttonClasses';

interface ProtectedRouteProps {
    children?: React.ReactNode;
}

export const ProtectedRoutes: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { isAuthenticated, mfaStatus, user } = useAuth();
    const rbacStatus = useAppSelector((s) => s.auth.rbacStatus);
    const rbacError = useAppSelector((s) => s.auth.rbacError);
    const dispatch = useAppDispatch();
    const location = useLocation();
    const [retryCount, setRetryCount] = React.useState(0);

    if (mfaStatus === 'setup_required') {
        return <Navigate to="/mfa/setup" state={{ from: location }} replace />;
    }

    if (mfaStatus === 'required') {
        return <Navigate to="/mfa/verify" state={{ from: location }} replace />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // First-hydration gate: authenticated but RBAC has never resolved.
    // The roles-emptiness clause keeps silent mid-session refetches (where
    // previous roles remain in state) from re-triggering the loader, even
    // though they transition rbacStatus through 'loading'.
    const hasNoRoles = (user?.roles?.length ?? 0) === 0;
    const isFirstHydration = hasNoRoles && rbacStatus !== 'ready';

    if (isFirstHydration && rbacStatus === 'error') {
        return (
            <AppLoader
                errorAction={
                    <>
                        <p className="text-sm text-mp-error">
                            We couldn&apos;t load your permissions.
                            {rbacError ? ` (${rbacError})` : ''}
                        </p>
                        {retryCount < 2 ? (
                            <button
                                type="button"
                                onClick={() => {
                                    setRetryCount((n) => n + 1);
                                    dispatch(resetRbacStatus());
                                    dispatch(getUserRBAC());
                                }}
                                className={buttonClassName({ size: 'none', rounded: false, className: 'mt-4 px-4 py-2 text-sm font-medium rounded' })}
                                data-testid="rbac-retry"
                            >
                                Try again
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => {
                                    // Defer to existing logout flow which clears state and
                                    // sends the user to /login. The Logout page calls
                                    // useAuth().logout() in its mount effect.
                                    window.location.assign('/logout');
                                }}
                                className={buttonClassName({ variant: 'secondary', size: 'none', rounded: false, className: 'mt-4 px-4 py-2 text-sm font-medium rounded' })}
                                data-testid="rbac-signout"
                            >
                                Sign out
                            </button>
                        )}
                    </>
                }
            />
        );
    }

    if (isFirstHydration) {
        return <AppLoader />;
    }

    return <AppLayout>{children || <Outlet />}</AppLayout>;
};
